<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container modal-md">
      <div class="modal-header">
        <h3>{{ mode === 'add' ? 'Add New Designation' : 'Edit Designation' }}</h3>
        <button type="button" class="modal-close" @click="$emit('close')">
          <i class="bx bx-x"></i>
        </button>
      </div>
      
      <form @submit.prevent="savePosition">
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label>Designation Code <span class="required">*</span></label>
              <input v-model="form.code" type="text" class="form-control" placeholder="e.g., MGR-001" required>
            </div>
            <div class="form-group">
              <label>Designation Name <span class="required">*</span></label>
              <input v-model="form.name" type="text" class="form-control" placeholder="e.g., Senior Manager" required>
            </div>
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea v-model="form.description" class="form-control" rows="3" placeholder="Enter designation description..."></textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Department</label>
              <select v-model="form.departmentId" class="form-control" @change="onDepartmentChange">
                <option value="">Select Department</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.departmentName || dept.name }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Section / Sub-vote</label>
              <select v-model="form.sectionId" class="form-control" :disabled="!form.departmentId" @change="onSectionChange">
                <option value="">{{ form.departmentId ? 'Select Section' : 'Select Department first' }}</option>
                <option v-for="sec in sections" :key="sec.id" :value="sec.id">
                  {{ sec.name }}{{ sec.code ? ' (' + sec.code + ')' : '' }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Unit / Directorate</label>
              <select v-model="form.unitId" class="form-control" :disabled="!form.sectionId">
                <option value="">{{ form.sectionId ? 'Select Unit' : 'Select Section first' }}</option>
                <option v-for="u in units" :key="u.id" :value="u.id">
                  {{ u.name }}{{ u.code ? ' (' + u.code + ')' : '' }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Supervisor</label>
              <select v-model="form.reportsTo" class="form-control">
                <option value="">None</option>
                <option v-for="pos in positions" :key="pos.id" :value="pos.id">
                  {{ pos.positionName || pos.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Level</label>
              <input v-model="form.level" type="number" min="1" max="10" class="form-control" placeholder="1-10">
            </div>
            <div class="form-group">
              <label>Job Group</label>
              <select v-model="form.jobGroup" class="form-control">
                <option value="">Select Job Group</option>
                <option v-for="jg in jobGroups" :key="jg.id" :value="jg.name">
                  {{ jg.code }} - {{ jg.name }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Status</label>
              <select v-model="form.status" class="form-control">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Min Salary (KES)</label>
              <input v-model="form.minSalary" type="number" step="0.01" class="form-control" placeholder="0.00">
            </div>
            <div class="form-group">
              <label>Max Salary (KES)</label>
              <input v-model="form.maxSalary" type="number" step="0.01" class="form-control" placeholder="0.00">
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Proposed</label>
              <input v-model="form.maxEmployees" type="number" min="0" class="form-control" placeholder="Proposed headcount">
            </div>
            <div class="form-group">
              <label>Inpost</label>
              <input :value="form.currentEmployees || 0" type="text" class="form-control" disabled readonly>
            </div>
            <div class="form-group">
              <label>Variance</label>
              <input :value="varianceValue" type="text" class="form-control" disabled readonly>
            </div>
          </div>

          <!-- Designation 98 Warning -->
          <div v-if="position && position.id === 98" class="warning-message">
            <i class="bx bx-shield"></i>
            <span>This is the Administrator designation (ID: 98). Handle with care.</span>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary" :disabled="saving">
            <i v-if="saving" class="bx bx-loader-alt bx-spin"></i>
            {{ saving ? 'Saving...' : 'Save Designation' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { authUrl, execute } from "@/api";

export default {
  name: "AddEditPositionModal",
  props: {
    position: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'add'
    },
    departments: {
      type: Array,
      default: () => []
    },
    positions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      saving: false,
      form: this.initForm(),
      jobGroups: [],
      sections: [],
      units: []
    };
  },
  computed: {
    varianceValue() {
      const proposed = Number(this.form.maxEmployees) || 0;
      const inpost = Number(this.form.currentEmployees) || 0;
      return proposed - inpost;
    }
  },
  mounted() {
    this.loadJobGroups();
  },
  watch: {
    position: {
      handler(val) {
        if (val) {
          this.form = {
            code: val.code || '',
            name: val.positionName || val.name || '',
            description: val.description || '',
            departmentId: val.department_id || val.departmentId || '',
            sectionId: val.section_id || val.sectionId || '',
            unitId: val.unit_id || val.unitId || '',
            reportsTo: val.reports_to || val.reportsTo || '',
            level: val.level || 1,
            jobGroup: val.job_group || val.jobGroup || '',
            status: val.is_active === 1 || val.is_active === true || val.status === 'active' ? 'active' : 'inactive',
            minSalary: val.salary_min || val.minSalary || '',
            maxSalary: val.salary_max || val.maxSalary || '',
            maxEmployees: val.max_employees || val.maxEmployees || '',
            currentEmployees: val.current_employees || val.currentEmployees || 0
          };
          if (this.form.departmentId) this.loadSections(this.form.departmentId);
          if (this.form.sectionId) this.loadUnits(this.form.sectionId);
        } else {
          this.form = this.initForm();
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    initForm() {
      return {
        code: '',
        name: '',
        description: '',
        departmentId: '',
        sectionId: '',
        unitId: '',
        reportsTo: '',
        level: 1,
        jobGroup: '',
        status: 'active',
        minSalary: '',
        maxSalary: '',
        maxEmployees: '',
        currentEmployees: 0
      };
    },

    onDepartmentChange() {
      this.form.sectionId = '';
      this.form.unitId = '';
      this.units = [];
      if (this.form.departmentId) this.loadSections(this.form.departmentId);
      else this.sections = [];
    },
    onSectionChange() {
      this.form.unitId = '';
      if (this.form.sectionId) this.loadUnits(this.form.sectionId);
      else this.units = [];
    },
    async loadSections(departmentId) {
      this.sections = [];
      const data = new FormData();
      data.append('function', 'getSectionsByDepartment');
      data.append('departmentId', departmentId);
      try {
        const res = await execute(data, authUrl);
        const list = res?.data?.sections ?? res?.data?.data?.sections ?? [];
        this.sections = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error('Load sections failed', e);
      }
    },
    async loadUnits(sectionId) {
      this.units = [];
      const data = new FormData();
      data.append('function', 'getUnitsBySection');
      data.append('sectionId', sectionId);
      try {
        const res = await execute(data, authUrl);
        const list = res?.data?.units ?? res?.data?.data?.units ?? [];
        this.units = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error('Load units failed', e);
      }
    },

    async loadJobGroups() {
      try {
        const data = new FormData();
        data.append("function", "getJobGroups");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          let raw = res.data.data?.jobGroups ?? res.data.jobGroups ?? [];
          if (raw && raw.Records) raw = raw.Records;
          if (!Array.isArray(raw) && raw) raw = [raw];
          this.jobGroups = raw || [];
        } else {
          this.jobGroups = [];
        }
      } catch (e) {
        console.error("Error loading job groups:", e);
        this.jobGroups = [];
      }
    },

    showToast(type, message) {
      if (this.$toast) {
        if (type === 'success') this.$toast.success(message);
        else if (type === 'error') this.$toast.error(message);
      } else {
        alert(message);
      }
    },

    validateForm() {
      if (!this.form.code || !this.form.code.trim()) {
        this.showToast('error', 'Position code is required');
        return false;
      }
      if (!this.form.name || !this.form.name.trim()) {
        this.showToast('error', 'Position name is required');
        return false;
      }
      if (this.form.minSalary && this.form.maxSalary && 
          parseFloat(this.form.minSalary) > parseFloat(this.form.maxSalary)) {
        this.showToast('error', 'Min salary cannot be greater than max salary');
        return false;
      }
      return true;
    },

    savePosition() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;

      const data = new FormData();
      data.append("function", this.mode === 'add' ? 'createPosition' : 'updatePosition');
      
      if (this.mode === 'edit' && this.position) {
        data.append("positionId", this.position.id);
      }
      
      data.append("code", this.form.code.trim());
      data.append("name", this.form.name.trim());
      data.append("description", this.form.description?.trim() || '');
      data.append("departmentId", this.form.departmentId || '');
      data.append("sectionId", this.form.sectionId || '');
      data.append("unitId", this.form.unitId || '');
      data.append("reportsTo", this.form.reportsTo || '');
      data.append("level", this.form.level || 1);
      data.append("jobGroup", this.form.jobGroup || '');
      data.append("status", this.form.status);
      data.append("minSalary", this.form.minSalary || 0);
      data.append("maxSalary", this.form.maxSalary || 0);
      data.append("maxEmployees", this.form.maxEmployees || 0);

      execute(data, authUrl)
          .then((res) => {
            this.saving = false;
            const body = res && res.data;
            if (body == null || body === '') {
              alert('Server returned no data. Check network and API URL.');
              return;
            }
            if (body.success === true || body.data?.positionId || body.positionId) {
              this.showToast('success', `Position ${this.mode === 'add' ? 'created' : 'updated'} successfully`);
              this.$emit('saved', body.data || { positionId: body.positionId, message: body.message });
              return;
            }
            const err = body.message || `Failed to ${this.mode} position`;
            alert(err);
          })
          .catch((e) => {
            this.saving = false;
            const msg = e?.response?.data?.message || (typeof e?.response?.data === 'string' ? e.response.data : null) || e?.message || 'Request failed';
            alert(msg);
          });
    }
  }
};
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;
}

/* Modal Container */
.modal-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
}

.modal-container.modal-md {
  max-width: 600px;
}

/* Modal Header */
.modal-header {
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 20px 20px 0 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}

.modal-header h3::before {
  content: '💼';
  font-size: 1.5rem;
  margin-right: 8px;
}

.modal-close {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 20px;
}

.modal-close:hover {
  background: #f1f5f9;
  color: #ef4444;
  transform: rotate(90deg);
}

/* Modal Body */
.modal-body {
  padding: 25px;
}

/* Form Elements */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 15px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
}

.required {
  color: #ef4444;
  margin-left: 2px;
}

.form-control {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.2s;
  background: #f8fafc;
}

.form-control:focus {
  outline: none;
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
  background: white;
}

.form-control:disabled,
.form-control[readonly] {
  background: #f1f5f9;
  color: #64748b;
  cursor: not-allowed;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

select.form-control {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  padding-right: 40px;
}

/* Warning Message */
.warning-message {
  margin-top: 20px;
  padding: 12px 16px;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 10px;
  color: #856404;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
}

.warning-message i {
  font-size: 20px;
  color: #f59e0b;
}

/* Modal Footer */
.modal-footer {
  padding: 20px 25px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #f8fafc;
  border-radius: 0 0 20px 20px;
}

/* Buttons */
.btn {
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #059669 0%, #16a34a 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.2);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(5, 150, 105, 0.3);
}

.btn-secondary {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover:not(:disabled) {
  background: #f1f5f9;
  color: #334155;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Scrollbar Styling */
.modal-container::-webkit-scrollbar {
  width: 8px;
}

.modal-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 10px;
}

.modal-container::-webkit-scrollbar-thumb {
  background: #94a3b8;
  border-radius: 10px;
}

.modal-container::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 10px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
  
  .modal-footer {
    flex-direction: column-reverse;
  }
  
  .btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .modal-body {
    padding: 15px;
  }
  
  .modal-header {
    padding: 15px;
  }
  
  .modal-footer {
    padding: 15px;
  }
}
</style>